import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Clock,
    User,
    Mail,
    PackageSearch,
    Percent,
    CreditCard,
    FileText,
} from "lucide-react";

import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { PageBreadcrumb } from "@/components/layout/PageBreadcrumb.tsx";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { formatDateForDisplay } from "@/lib/utils";
import {
    getOrderById,
    formatTotal,
    extractCustomerTagValue,
    extractCashDiscountPercent,
    parseItemOptions,
    extractItemAttributesFromTags,
    getOrderStatusLabel,
    type OrderRecord,
} from "@/lib/orders";
import {
    fetchOrderById,
    fetchProducts,
    fetchCustomerById,
    fetchCustomers,
    parseDiscountPercentFromGroupName,
} from "@/lib/api";
import { getProductAvailability } from "@/lib/availability";
import { type ClientRecord } from "@/lib/mockClients";

// ----------------------------------------------------------------------------

interface OrdineDettaglioProps {
    className?: string;
}

export function OrdineDettaglio(props: OrdineDettaglioProps) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { className } = props;

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const { id } = useParams();

    // -------------------------------------
    // Effects
    // -------------------------------------

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    const [order, setOrder] = React.useState<OrderRecord | undefined>(() =>
        id ? getOrderById(id) : undefined,
    );
    const [client, setClient] = React.useState<ClientRecord | null>(null);
    const [isLoading, setIsLoading] = React.useState(!order);
    const [resolvedImages, setResolvedImages] = React.useState<
        Record<string, string>
    >({});
    const [resolvedProducts, setResolvedProducts] = React.useState<
        Record<
            string,
            {
                title?: string;
                tags?: string[];
                stockCount?: number;
                unlimited?: number;
            }
        >
    >({});

    React.useEffect(() => {
        let isMounted = true;
        if (!id) return;
        const targetOrderId: string = id;

        async function loadOrder() {
            try {
                const fetchedOrder = await fetchOrderById(targetOrderId);
                if (isMounted && fetchedOrder) {
                    const local = getOrderById(targetOrderId);
                    setOrder({
                        ...fetchedOrder,
                        customerName:
                            fetchedOrder.customerName ||
                            local?.customerName ||
                            "",
                        email: fetchedOrder.email || local?.email || "",
                        clientId:
                            fetchedOrder.clientId || local?.clientId || "",
                        customerDiscountPercent:
                            fetchedOrder.customerDiscountPercent ??
                            local?.customerDiscountPercent,
                        customerTags:
                            fetchedOrder.customerTags ?? local?.customerTags,
                        customerGroupName:
                            fetchedOrder.customerGroupName ??
                            local?.customerGroupName,
                        paymentTag:
                            fetchedOrder.paymentTag || local?.paymentTag,
                        paydescriptionTag:
                            fetchedOrder.paydescriptionTag ||
                            local?.paydescriptionTag,
                    });
                }
            } catch (err) {
                console.error("[OrdineDettaglio] fetchOrderById failed:", err);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        }

        void loadOrder();

        return () => {
            isMounted = false;
        };
    }, [id]);

    React.useEffect(() => {
        if (!order) return;
        let isMounted = true;

        async function loadCustomer() {
            const identifier = order?.clientId || order?.email;
            if (!identifier) return;
            try {
                const found = await fetchCustomerById(identifier);
                if (isMounted && found) {
                    setClient(found);
                }
            } catch (err) {
                console.warn(
                    "[OrdineDettaglio] Failed to fetch customer:",
                    err,
                );
            }
        }

        void loadCustomer();

        return () => {
            isMounted = false;
        };
    }, [order?.clientId, order?.email]);

    React.useEffect(() => {
        if (!order || order.items.length === 0) return;
        const currentItems = order.items;

        let isMounted = true;

        async function loadProductDetails() {
            const imgMap: Record<string, string> = {};
            const prodMap: Record<
                string,
                {
                    title?: string;
                    tags?: string[];
                    stockCount?: number;
                    unlimited?: number;
                }
            > = {};

            const promises = currentItems.map(async (item) => {
                try {
                    const res = await fetchProducts(item.code);
                    const matched = res.products.find(
                        (p) => p.sku === item.code || p.code === item.code,
                    );
                    if (matched) {
                        if (matched.imageUrl) {
                            imgMap[item.code] = matched.imageUrl;
                        }
                        prodMap[item.code] = {
                            title: matched.name,
                            tags: matched.tags,
                            stockCount: matched.stockCount,
                            unlimited: matched.unlimited,
                        };
                    }
                } catch {
                    // ignore
                }
            });

            await Promise.all(promises);
            if (isMounted) {
                setResolvedImages(imgMap);
                setResolvedProducts(prodMap);
            }
        }

        void loadProductDetails();

        return () => {
            isMounted = false;
        };
    }, [order]);

    const restockingItems = React.useMemo(() => {
        if (!order) return [];
        return order.items
            .map((item) => {
                const itemTags = item.tags || resolvedProducts[item.code]?.tags;
                const itemStockCount =
                    item.stockCount ?? resolvedProducts[item.code]?.stockCount;
                const itemUnlimited =
                    item.unlimited ?? resolvedProducts[item.code]?.unlimited;
                return {
                    item,
                    availability: getProductAvailability({
                        tags: itemTags,
                        stockCount: itemStockCount,
                        unlimited: itemUnlimited,
                    }),
                };
            })
            .filter((entry) => entry.availability.caseType === "restocking");
    }, [order, resolvedProducts]);

    if (isLoading) {
        return (
            <DashboardLayout className={className}>
                <div className="w-full flex flex-col items-center justify-center min-h-[400px] gap-3">
                    <div className="w-8 h-8 rounded-full border-4 border-[#0D6EFD] border-t-transparent animate-spin" />
                    <span className="text-sm text-gray-500">
                        Caricamento dettagli ordine...
                    </span>
                </div>
            </DashboardLayout>
        );
    }

    if (!order) {
        return <Navigate to="/ordini" replace />;
    }

    // Client information is retrieved from the order record directly
    const clientName = client?.name || order.customerName;
    const clientEmail = client?.email || order.email;

    const cashDiscountPercent =
        order.cashDiscountPercent ??
        extractCashDiscountPercent(order.customerTags) ??
        extractCashDiscountPercent(client?.tags);

    const itemsSubtotal = React.useMemo(() => {
        return order.items.reduce(
            (sum, item) => sum + item.price * item.quantity,
            0,
        );
    }, [order.items]);

    const cashDiscountAmount = React.useMemo(() => {
        if (!cashDiscountPercent || cashDiscountPercent <= 0) return 0;
        return (itemsSubtotal * cashDiscountPercent) / 100;
    }, [itemsSubtotal, cashDiscountPercent]);

    const discountPercent =
        client?.discountPercent ??
        order.customerDiscountPercent ??
        (client?.groupName
            ? parseDiscountPercentFromGroupName(client.groupName)
            : undefined);

    const discountText =
        discountPercent !== undefined
            ? `Sconto ${discountPercent}%`
            : client?.groupName
              ? client.groupName
              : "Sconto 0%";

    const paymentTagValue =
        extractCustomerTagValue(client?.tags, "payment") ||
        order.paymentTag ||
        extractCustomerTagValue(order.customerTags, "payment") ||
        "-";

    const paydescriptionTagValue =
        extractCustomerTagValue(client?.tags, "paydescription") ||
        order.paydescriptionTag ||
        extractCustomerTagValue(order.customerTags, "paydescription") ||
        "-";

    return (
        <DashboardLayout className={className}>
            <div className="w-full">
                <PageBreadcrumb
                    items={[
                        { label: "Home", to: "/" },
                        { label: "Ordini", to: "/ordini" },
                        { label: `Ordine #${order.id}` },
                    ]}
                />

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 border-gray-200"
                            asChild>
                            <Link to="/ordini">
                                <ArrowLeft className="w-4 h-4 text-gray-500" />
                                <span className="sr-only">Indietro</span>
                            </Link>
                        </Button>
                        <div>
                            <div className="flex items-center gap-2.5">
                                <h1 className="text-2xl font-bold text-gray-900">
                                    Ordine #{order.id}
                                </h1>
                                {(order.notes || order.message) && (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-900 border border-amber-300">
                                        <FileText className="w-3 h-3 text-amber-700" />
                                        Con note
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-gray-500">
                                Creato il {formatDateForDisplay(order.date)}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-6">
                    {/* Left Column: Order Items */}
                    <div className="space-y-6">
                        <Card className="shadow-sm border-gray-100">
                            <CardHeader>
                                <CardTitle className="text-lg font-bold">
                                    Prodotti ordinati
                                </CardTitle>
                                <CardDescription>
                                    Elenco dei prodotti inseriti in questo
                                    ordine.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="p-0 sm:p-6">
                                {/* Desktop Table View */}
                                <div className="hidden sm:block overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="hover:bg-transparent border-gray-100">
                                                <TableHead className="w-16 font-medium text-gray-900">
                                                    Foto
                                                </TableHead>
                                                <TableHead className="font-medium text-gray-900">
                                                    Prodotto
                                                </TableHead>
                                                <TableHead className="font-medium text-gray-900 text-right">
                                                    Prezzo
                                                </TableHead>
                                                <TableHead className="font-medium text-gray-900 text-center">
                                                    Quantità
                                                </TableHead>
                                                <TableHead className="font-medium text-gray-900">
                                                    Specifiche & Note
                                                </TableHead>
                                                <TableHead className="font-medium text-gray-900 text-right">
                                                    Totale
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {order.items.map((item) => (
                                                <TableRow
                                                    key={item.id}
                                                    className="border-gray-100">
                                                    <TableCell className="py-2.5 align-top">
                                                        {item.imageUrl ||
                                                        resolvedImages[
                                                            item.code
                                                        ] ? (
                                                            <img
                                                                src={
                                                                    item.imageUrl ||
                                                                    resolvedImages[
                                                                        item
                                                                            .code
                                                                    ]
                                                                }
                                                                alt={item.code}
                                                                className="w-12 h-12 rounded-lg object-cover border border-gray-100 bg-gray-50"
                                                            />
                                                        ) : (
                                                            <div className="w-12 h-12 rounded-lg border border-gray-100 bg-gray-50 flex items-center justify-center text-gray-400">
                                                                <PackageSearch className="w-5 h-5 text-gray-300" />
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="align-top py-3 font-medium">
                                                        <div className="font-bold text-gray-900 leading-tight">
                                                            {item.code}
                                                        </div>
                                                        {(item.title ||
                                                            resolvedProducts[
                                                                item.code
                                                            ]?.title) && (
                                                            <div className="text-xs text-gray-500 font-normal mt-1 line-clamp-2 max-w-[260px]">
                                                                {item.title ||
                                                                    resolvedProducts[
                                                                        item
                                                                            .code
                                                                    ]?.title}
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="align-top py-3 text-right text-gray-900">
                                                        {formatTotal(
                                                            item.price,
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="align-top py-3 text-center text-gray-900 font-medium">
                                                        {item.quantity}
                                                    </TableCell>
                                                    <TableCell className="align-top py-3">
                                                        {(() => {
                                                            const itemTags =
                                                                item.tags ||
                                                                resolvedProducts[
                                                                    item.code
                                                                ]?.tags;
                                                            const fromTags =
                                                                extractItemAttributesFromTags(
                                                                    itemTags,
                                                                );
                                                            const parsed =
                                                                parseItemOptions(
                                                                    item.options ||
                                                                        item.note,
                                                                );

                                                            const color =
                                                                (item.attributes
                                                                    ?.color &&
                                                                    item
                                                                        .attributes
                                                                        .color !==
                                                                        "Standard" &&
                                                                    item.attributes.color.trim()) ||
                                                                parsed
                                                                    .attributes
                                                                    .color ||
                                                                fromTags.color;
                                                            const temp =
                                                                (item.attributes
                                                                    ?.temperature &&
                                                                    item
                                                                        .attributes
                                                                        .temperature !==
                                                                        "Standard" &&
                                                                    item.attributes.temperature.trim()) ||
                                                                parsed
                                                                    .attributes
                                                                    .temperature ||
                                                                fromTags.temperature;
                                                            const size =
                                                                (item.attributes
                                                                    ?.size &&
                                                                    item
                                                                        .attributes
                                                                        .size !==
                                                                        "Standard" &&
                                                                    item.attributes.size.trim()) ||
                                                                parsed
                                                                    .attributes
                                                                    .size ||
                                                                fromTags.size;

                                                            let displayNote:
                                                                | string
                                                                | undefined =
                                                                undefined;
                                                            if (
                                                                parsed.note &&
                                                                parsed.note.trim() &&
                                                                parsed.note.toLowerCase() !==
                                                                    "standard"
                                                            ) {
                                                                displayNote =
                                                                    parsed.note.trim();
                                                            } else if (
                                                                item.note &&
                                                                typeof item.note ===
                                                                    "string"
                                                            ) {
                                                                const cleaned =
                                                                    item.note
                                                                        .replace(
                                                                            /^not[ea]\s*[:=-]\s*/i,
                                                                            "",
                                                                        )
                                                                        .trim();
                                                                if (
                                                                    cleaned &&
                                                                    cleaned.toLowerCase() !==
                                                                        "standard" &&
                                                                    !cleaned.startsWith(
                                                                        "Colore:",
                                                                    ) &&
                                                                    !cleaned.startsWith(
                                                                        "Temperatura:",
                                                                    ) &&
                                                                    !cleaned.startsWith(
                                                                        "Misura:",
                                                                    )
                                                                ) {
                                                                    displayNote =
                                                                        cleaned;
                                                                }
                                                            } else if (
                                                                item.options &&
                                                                typeof item.options ===
                                                                    "string" &&
                                                                item.options !==
                                                                    item.note
                                                            ) {
                                                                const cleaned =
                                                                    item.options
                                                                        .replace(
                                                                            /^not[ea]\s*[:=-]\s*/i,
                                                                            "",
                                                                        )
                                                                        .trim();
                                                                if (
                                                                    cleaned &&
                                                                    cleaned.toLowerCase() !==
                                                                        "standard" &&
                                                                    !cleaned.startsWith(
                                                                        "Colore:",
                                                                    ) &&
                                                                    !cleaned.startsWith(
                                                                        "Temperatura:",
                                                                    ) &&
                                                                    !cleaned.startsWith(
                                                                        "Misura:",
                                                                    )
                                                                ) {
                                                                    displayNote =
                                                                        cleaned;
                                                                }
                                                            }

                                                            const hasVariants =
                                                                Boolean(
                                                                    color ||
                                                                    temp ||
                                                                    size,
                                                                );

                                                            return (
                                                                <div className="space-y-1.5">
                                                                    {hasVariants && (
                                                                        <div className="text-xs text-gray-500 flex flex-wrap gap-1.5">
                                                                            {color && (
                                                                                <span className="bg-gray-100 px-2 py-0.5 rounded font-medium text-gray-700">
                                                                                    {
                                                                                        color
                                                                                    }
                                                                                </span>
                                                                            )}
                                                                            {temp && (
                                                                                <span className="bg-gray-100 px-2 py-0.5 rounded font-medium text-gray-700">
                                                                                    {
                                                                                        temp
                                                                                    }
                                                                                </span>
                                                                            )}
                                                                            {size && (
                                                                                <span className="bg-gray-100 px-2 py-0.5 rounded font-medium text-gray-700">
                                                                                    {
                                                                                        size
                                                                                    }
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    {displayNote && (
                                                                        <div className="text-[11px] text-amber-900 bg-amber-50/90 border border-amber-200/80 px-2.5 py-1.5 rounded-md inline-flex items-start gap-1.5 max-w-full shadow-2xs">
                                                                            <div className="break-words">
                                                                                <span className="font-bold text-amber-950">
                                                                                    Nota:
                                                                                </span>{" "}
                                                                                <span className="font-medium text-amber-900">
                                                                                    {
                                                                                        displayNote
                                                                                    }
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}
                                                        {(() => {
                                                            const itemTags =
                                                                item.tags ||
                                                                resolvedProducts[
                                                                    item.code
                                                                ]?.tags;
                                                            const itemStockCount =
                                                                item.stockCount ??
                                                                resolvedProducts[
                                                                    item.code
                                                                ]?.stockCount;
                                                            const itemUnlimited =
                                                                item.unlimited ??
                                                                resolvedProducts[
                                                                    item.code
                                                                ]?.unlimited;
                                                            const availability =
                                                                getProductAvailability(
                                                                    {
                                                                        tags: itemTags,
                                                                        stockCount:
                                                                            itemStockCount,
                                                                        unlimited:
                                                                            itemUnlimited,
                                                                    },
                                                                );
                                                            if (
                                                                availability.caseType ===
                                                                "restocking"
                                                            ) {
                                                                return (
                                                                    <div className="mt-1.5 flex items-center gap-1.5 px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-950 text-[11px] font-medium w-fit">
                                                                        <Clock className="w-3 h-3 text-amber-600 shrink-0" />
                                                                        <span>
                                                                            In
                                                                            riassortimento
                                                                            —
                                                                            Data
                                                                            presunto
                                                                            arrivo:{" "}
                                                                            <strong className="font-bold text-amber-950">
                                                                                {availability.restockDate ||
                                                                                    "In attesa"}
                                                                            </strong>
                                                                        </span>
                                                                    </div>
                                                                );
                                                            }
                                                            return null;
                                                        })()}
                                                    </TableCell>
                                                    <TableCell className="align-top py-3 text-right font-bold text-gray-900">
                                                        {formatTotal(
                                                            item.price *
                                                                item.quantity,
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>

                                {/* Mobile List View */}
                                <div className="sm:hidden space-y-4 p-4">
                                    {order.items.map((item) => (
                                        <div
                                            key={item.id}
                                            className="flex gap-3 border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                                            <div className="shrink-0">
                                                {item.imageUrl ||
                                                resolvedImages[item.code] ? (
                                                    <img
                                                        src={
                                                            item.imageUrl ||
                                                            resolvedImages[
                                                                item.code
                                                            ]
                                                        }
                                                        alt={item.code}
                                                        className="w-16 h-16 rounded-lg object-cover border border-gray-100 bg-gray-50"
                                                    />
                                                ) : (
                                                    <div className="w-16 h-16 rounded-lg border border-gray-100 bg-gray-50 flex items-center justify-center text-gray-400">
                                                        <PackageSearch className="w-6 h-6 text-gray-300" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex justify-between items-start mb-1">
                                                    <div className="min-w-0 pr-2">
                                                        <span className="font-bold text-gray-900 text-sm block truncate">
                                                            {item.code}
                                                        </span>
                                                        {(item.title ||
                                                            resolvedProducts[
                                                                item.code
                                                            ]?.title) && (
                                                            <span className="text-xs text-gray-500 font-normal block truncate">
                                                                {item.title ||
                                                                    resolvedProducts[
                                                                        item
                                                                            .code
                                                                    ]?.title}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="font-bold text-gray-900 text-sm shrink-0">
                                                        {formatTotal(
                                                            item.price *
                                                                item.quantity,
                                                        )}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-gray-500 space-y-1">
                                                    {(() => {
                                                        const itemTags =
                                                            item.tags ||
                                                            resolvedProducts[
                                                                item.code
                                                            ]?.tags;
                                                        const fromTags =
                                                            extractItemAttributesFromTags(
                                                                itemTags,
                                                            );
                                                        const parsed =
                                                            parseItemOptions(
                                                                item.options ||
                                                                    item.note,
                                                            );

                                                        const color =
                                                            (item.attributes
                                                                ?.color &&
                                                                item.attributes
                                                                    .color !==
                                                                    "Standard" &&
                                                                item.attributes.color.trim()) ||
                                                            parsed.attributes
                                                                .color ||
                                                            fromTags.color;
                                                        const temp =
                                                            (item.attributes
                                                                ?.temperature &&
                                                                item.attributes
                                                                    .temperature !==
                                                                    "Standard" &&
                                                                item.attributes.temperature.trim()) ||
                                                            parsed.attributes
                                                                .temperature ||
                                                            fromTags.temperature;
                                                        const size =
                                                            (item.attributes
                                                                ?.size &&
                                                                item.attributes
                                                                    .size !==
                                                                    "Standard" &&
                                                                item.attributes.size.trim()) ||
                                                            parsed.attributes
                                                                .size ||
                                                            fromTags.size;

                                                        let displayNote:
                                                            | string
                                                            | undefined =
                                                            undefined;
                                                        if (
                                                            parsed.note &&
                                                            parsed.note.trim() &&
                                                            parsed.note.toLowerCase() !==
                                                                "standard"
                                                        ) {
                                                            displayNote =
                                                                parsed.note.trim();
                                                        } else if (
                                                            item.note &&
                                                            typeof item.note ===
                                                                "string"
                                                        ) {
                                                            const cleaned =
                                                                item.note
                                                                    .replace(
                                                                        /^not[ea]\s*[:=-]\s*/i,
                                                                        "",
                                                                    )
                                                                    .trim();
                                                            if (
                                                                cleaned &&
                                                                cleaned.toLowerCase() !==
                                                                    "standard" &&
                                                                !cleaned.startsWith(
                                                                    "Colore:",
                                                                ) &&
                                                                !cleaned.startsWith(
                                                                    "Temperatura:",
                                                                ) &&
                                                                !cleaned.startsWith(
                                                                    "Misura:",
                                                                )
                                                            ) {
                                                                displayNote =
                                                                    cleaned;
                                                            }
                                                        } else if (
                                                            item.options &&
                                                            typeof item.options ===
                                                                "string" &&
                                                            item.options !==
                                                                item.note
                                                        ) {
                                                            const cleaned =
                                                                item.options
                                                                    .replace(
                                                                        /^not[ea]\s*[:=-]\s*/i,
                                                                        "",
                                                                    )
                                                                    .trim();
                                                            if (
                                                                cleaned &&
                                                                cleaned.toLowerCase() !==
                                                                    "standard" &&
                                                                !cleaned.startsWith(
                                                                    "Colore:",
                                                                ) &&
                                                                !cleaned.startsWith(
                                                                    "Temperatura:",
                                                                ) &&
                                                                !cleaned.startsWith(
                                                                    "Misura:",
                                                                )
                                                            ) {
                                                                displayNote =
                                                                    cleaned;
                                                            }
                                                        }

                                                        const hasVariants =
                                                            Boolean(
                                                                color ||
                                                                temp ||
                                                                size,
                                                            );

                                                        return (
                                                            <>
                                                                {hasVariants && (
                                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                                        {color && (
                                                                            <span className="bg-gray-100 px-1.5 py-0.5 rounded font-medium text-gray-700">
                                                                                {
                                                                                    color
                                                                                }
                                                                            </span>
                                                                        )}
                                                                        {temp && (
                                                                            <span className="bg-gray-100 px-1.5 py-0.5 rounded font-medium text-gray-700">
                                                                                {
                                                                                    temp
                                                                                }
                                                                            </span>
                                                                        )}
                                                                        {size && (
                                                                            <span className="bg-gray-100 px-1.5 py-0.5 rounded font-medium text-gray-700">
                                                                                {
                                                                                    size
                                                                                }
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                {displayNote && (
                                                                    <div className="text-[11px] text-amber-900 bg-amber-50/90 border border-amber-200/80 p-2 rounded-md mt-1.5 flex items-start gap-1.5 shadow-2xs">
                                                                        <FileText className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                                                                        <div className="break-words">
                                                                            <span className="font-bold text-amber-950">
                                                                                Nota:
                                                                            </span>{" "}
                                                                            <span className="font-medium text-amber-900">
                                                                                {
                                                                                    displayNote
                                                                                }
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                    {(() => {
                                                        const itemTags =
                                                            item.tags ||
                                                            resolvedProducts[
                                                                item.code
                                                            ]?.tags;
                                                        const itemStockCount =
                                                            item.stockCount ??
                                                            resolvedProducts[
                                                                item.code
                                                            ]?.stockCount;
                                                        const itemUnlimited =
                                                            item.unlimited ??
                                                            resolvedProducts[
                                                                item.code
                                                            ]?.unlimited;
                                                        const availability =
                                                            getProductAvailability(
                                                                {
                                                                    tags: itemTags,
                                                                    stockCount:
                                                                        itemStockCount,
                                                                    unlimited:
                                                                        itemUnlimited,
                                                                },
                                                            );
                                                        if (
                                                            availability.caseType ===
                                                            "restocking"
                                                        ) {
                                                            return (
                                                                <div className="mt-1.5 flex items-center gap-1.5 px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-950 text-[11px] font-medium">
                                                                    <Clock className="w-3 h-3 text-amber-600 shrink-0" />
                                                                    <span>
                                                                        In
                                                                        riassortimento
                                                                        — Data
                                                                        presunto
                                                                        arrivo:{" "}
                                                                        <strong className="font-bold text-amber-950">
                                                                            {availability.restockDate ||
                                                                                "In attesa"}
                                                                        </strong>
                                                                    </span>
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    })()}
                                                    <p className="pt-1.5">
                                                        Prezzo unitario:{" "}
                                                        {formatTotal(
                                                            item.price,
                                                        )}
                                                    </p>
                                                    <p>
                                                        Quantità:{" "}
                                                        {item.quantity}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Total section */}
                                {cashDiscountPercent !== undefined &&
                                cashDiscountPercent > 0 ? (
                                    <div className="space-y-2.5 px-4 py-4 sm:px-0 sm:pb-0 sm:pt-6 border-t border-gray-100 mt-4">
                                        <div className="flex justify-between items-center text-sm text-gray-600">
                                            <span>Subtotale articoli</span>
                                            <span className="font-medium text-gray-900">
                                                {formatTotal(itemsSubtotal)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm text-emerald-700">
                                            <span className="flex items-center gap-1.5">
                                                <span>Sconto piede</span>
                                                <span className="text-[11px] font-semibold bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-200">
                                                    -{cashDiscountPercent}%
                                                </span>
                                            </span>
                                            <span className="font-semibold">
                                                -
                                                {formatTotal(
                                                    cashDiscountAmount,
                                                )}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                                            <span className="text-lg font-bold text-gray-900">
                                                Totale ordine
                                            </span>
                                            <span className="text-2xl font-bold text-gray-900">
                                                {order.total}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex justify-between items-center px-4 py-4 sm:px-0 sm:pb-0 sm:pt-6 border-t border-gray-100 mt-4">
                                        <span className="text-lg font-bold text-gray-900">
                                            Totale ordine
                                        </span>
                                        <span className="text-2xl font-bold text-gray-900">
                                            {order.total}
                                        </span>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right Column: Customer Details */}
                    <div className="space-y-6">
                        <Card className="shadow-sm border-gray-100">
                            <CardHeader>
                                <CardTitle className="text-lg font-bold">
                                    Dettagli cliente
                                </CardTitle>
                                <CardDescription>
                                    Informazioni sull'acquirente dell'ordine.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <User className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-xs text-gray-400">
                                            Nome Cliente
                                        </div>
                                        {order.clientId ? (
                                            <Link
                                                to={`/clienti/${order.clientId}`}
                                                className="text-sm font-semibold text-[#0D6EFD] hover:underline">
                                                {clientName}
                                            </Link>
                                        ) : (
                                            <div className="text-sm font-semibold text-gray-900">
                                                {clientName}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <Mail className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-xs text-gray-400">
                                            Email
                                        </div>
                                        <div className="text-sm font-medium text-gray-900">
                                            {clientEmail}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <Percent className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-xs text-gray-400">
                                            Scontistica
                                        </div>
                                        <div className="text-sm font-semibold text-gray-900 flex flex-wrap items-center gap-1.5 mt-0.5">
                                            <span>{discountText}</span>
                                            {cashDiscountPercent !==
                                                undefined &&
                                                cashDiscountPercent > 0 && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                                        Sconto piede:{" "}
                                                        {cashDiscountPercent}%
                                                    </span>
                                                )}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <CreditCard className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-xs text-gray-400">
                                            Pagamento
                                        </div>
                                        <div className="text-sm font-semibold text-gray-900">
                                            {paymentTagValue}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <FileText className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                                    <div>
                                        <div className="text-xs text-gray-400">
                                            Descrizione pagamento
                                        </div>
                                        <div className="text-sm font-semibold text-gray-900">
                                            {paydescriptionTagValue}
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="shadow-sm border-gray-100">
                            <CardHeader>
                                <CardTitle className="text-lg font-bold">
                                    Stato ordine
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                            Number(order.status) === 2 ||
                                            !order.status
                                                ? "bg-blue-100 text-blue-800"
                                                : Number(order.status) === 1
                                                  ? "bg-emerald-100 text-emerald-800"
                                                  : Number(order.status) === 0
                                                    ? "bg-amber-100 text-amber-800"
                                                    : Number(order.status) === 3
                                                      ? "bg-purple-100 text-purple-800"
                                                      : "bg-gray-100 text-gray-800"
                                        }`}>
                                        {getOrderStatusLabel(order.status)}
                                    </span>
                                </div>
                            </CardContent>
                        </Card>

                        {(order.notes || order.message) && (
                            <Card className="shadow-sm border-amber-200/80 bg-amber-50/40">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base font-bold flex items-center text-amber-950">
                                        <FileText className="w-4 h-4 text-amber-600 shrink-0" />
                                        Note generali ordine
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-xs text-amber-950 bg-white p-3.5 rounded-lg border border-amber-200/70 whitespace-pre-wrap leading-relaxed shadow-2xs font-medium">
                                        {order.notes || order.message}
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}
